import { StacksNetwork } from '@stacks/network';
import { isEmpty } from 'lodash';
import { hexToValue } from 'micro-stacks/clarity';
import { CollectionNFTsPageData, NftHolding, NFTImage, NftSafeState } from 'types/NftTypes';

import { NftImagesStore } from '../store';

import {
  AddressBalance,
  getNFTHoldings,
  getNFTjson,
  getNftTokenUri,
  replaceIPFSimage,
} from './index';

export const getNftImageById = async (
  collectionContract: string,
  nftId: string,
  walletAccount: string | null,
  stacksNetwork: StacksNetwork,
  nftImagesStore: NftImagesStore
): Promise<NFTImage | null> => {
  if (!walletAccount) return null;
  const savedNft = nftImagesStore.nftImages.get(nftId);

  if (savedNft) {
    return Promise.resolve(savedNft);
  }

  const nftUri = await getNftTokenUri(stacksNetwork, collectionContract, nftId, walletAccount);
  if (!nftUri)
    return {
      image: '',
      title: nftId,
      id: nftId,
      isVideo: false,
    };

  // для not-punk коллекции не работает подгрузка через https://dweb.link/ipfs/
  const nftJson = await getNFTjson(nftUri, nftId, collectionContract.endsWith('not-punk'));

  let res: NFTImage | null = null;

  if (!nftJson) {
    res = {
      image: '',
      title: '',
      id: nftId,
      isVideo: false,
    };
  } else {
    res = {
      image: replaceIPFSimage(nftJson.image ? nftJson.image : nftJson.video),
      title: nftJson.name,
      id: nftId,
      isVideo: !nftJson.image,
    };
  }

  if (Boolean(res)) {
    nftImagesStore.saveNft(res);
  }

  return res;
};

const getNftImages = async (
  contract: string,
  hex: string,
  collectionsWithIds: NftSafeState[],
  walletAccount: string | null,
  stacksNetwork: StacksNetwork,
  nftImagesStore: NftImagesStore
): Promise<void> => {
  const [address, collectionName] = contract.split('::');
  const nftId = hexToValue(hex) as string;
  await Promise.race([
    getNftImageById(address, nftId, walletAccount, stacksNetwork, nftImagesStore),
    new Promise<NFTImage | null>((resolve) => setTimeout(() => resolve(null), 2000)),
  ]).then((res) => {
    if (res) collectionsWithIds.push({ title: collectionName, addr: address, images: [res] });
  });
};

export const getAllCollectionNftIds = async (
  safeAddress: string,
  stacksNetwork: StacksNetwork,
  nftContract: string
) => {
  let offset = 0;
  const resultIds: string[] = [];

  while (true) {
    const result = await getNFTHoldings(stacksNetwork, safeAddress, nftContract, 50, offset);

    if (isEmpty(result.results)) {
      break;
    }
    result.results.forEach((item: any) => {
      resultIds.push(hexToValue(item.value.hex));
    });
    offset += 50;
  }

  return resultIds;
};

export const getNftCollections = async (
  imagesLimit: number,
  walletAccount: string | null,
  safeAddress: string,
  stacksNetwork: StacksNetwork,
  balance: Pick<AddressBalance, 'non_fungible_tokens'>,
  nftImagesStore: NftImagesStore,
  nftContract?: string,
  offset?: number
) => {
  const collectionsWithIds: NftSafeState[] = [];
  const batchSize = 10;
  const contracts = Object.keys(balance.non_fungible_tokens).filter(
    (token) => Number(balance.non_fungible_tokens[token].count) > 0
  );
  for (const contract of contracts) {
    if (nftContract && nftContract !== contract) {
      continue;
    }
    const holdings = await getNFTHoldings(
      stacksNetwork,
      safeAddress,
      contract,
      imagesLimit,
      offset || 0
    );

    const totalBatches = Math.ceil(holdings.results.length / batchSize);
    const batches = Array.from({ length: totalBatches }, (_, index) => {
      const startIndex = index * batchSize;
      const endIndex = startIndex + batchSize;

      return holdings.results.slice(startIndex, endIndex);
    });

    for (const batch of batches) {
      const batchPromises = batch.map(
        async (url: NftHolding) =>
          await getNftImages(
            contract,
            url.value.hex,
            collectionsWithIds,
            walletAccount,
            stacksNetwork,
            nftImagesStore
          )
      );
      await Promise.all(batchPromises);
    }
  }
  const convertedCollections: NftSafeState[] = [];
  collectionsWithIds.forEach((collection) => {
    const filteredItemIndex = convertedCollections.findIndex(
      (item) => item.addr === collection.addr && item.title === collection.title
    );
    if (filteredItemIndex >= 0) {
      convertedCollections[filteredItemIndex].images = [
        ...convertedCollections[filteredItemIndex].images,
        ...collection.images,
      ];
    } else {
      convertedCollections.push(collection);
    }
  });

  return convertedCollections;
};

export const getCollectionPaginatedNFTsData = async (
  nftCollectionId: string,
  limit: string,
  offset: string,
  walletAccount: string | null,
  safeAddress: string,
  stacksNetwork: StacksNetwork,
  balance: Pick<AddressBalance, 'non_fungible_tokens'>,
  nftImagesStore: NftImagesStore,
  onlyResults?: boolean
): Promise<CollectionNFTsPageData | NFTImage[] | null> => {
  const holdings = await getNftCollections(
    Number(limit),
    walletAccount,
    safeAddress,
    stacksNetwork,
    balance,
    nftImagesStore,
    nftCollectionId,
    Number(offset)
  );
  const contract = Object.keys(balance.non_fungible_tokens).find(
    (token) => nftCollectionId === token
  );
  if (isEmpty(holdings) || !contract) return null;
  if (onlyResults) return holdings[0]?.images;

  return {
    title: holdings[0].title,
    addr: holdings[0].addr,
    limit: limit,
    offset: offset,
    total: +balance.non_fungible_tokens[contract].count,
    results: holdings[0]?.images,
  };
};

export const getNftCollectionId = (address: string, title: string) => {
  return `${address}::${title}`;
};
